import React from 'react';
import { motion } from 'framer-motion';
import { MoonStar, Activity } from 'lucide-react';
export function Chapter5() {
  return <section id="capitulo-5" className="min-h-screen py-24 px-6 lg:px-16 bg-cream relative" data-id="element-195">
      <div className="max-w-4xl mx-auto" data-id="element-196">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-197">
          <h2 className="text-gold font-medium tracking-widest uppercase text-sm mb-4" data-id="element-198">
            Capítulo 5
          </h2>
          <h3 className="font-serif text-4xl lg:text-5xl text-darkBrown font-bold mb-6 leading-tight" data-id="element-199">
            Drenaje Linfático y Sueño
          </h3>
          <p className="text-xl text-darkBrown/80 text-balance" data-id="element-200">
            La alimentación es solo la mitad de la ecuación. Para desinflamar,
            tu cuerpo necesita eliminar toxinas y descansar profundamente.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-201">
          {/* Lymphatic Drainage */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-202">
            <div className="flex items-center gap-4 mb-6" data-id="element-203">
              <div className="p-3 bg-gold/20 rounded-xl text-deepGold" data-id="element-204">
                <Activity className="w-8 h-8" data-id="element-205" />
              </div>
              <h4 className="font-serif text-3xl font-bold text-darkBrown" data-id="element-206">
                La Posición en "L"
              </h4>
            </div>

            <p className="text-darkBrown/80 mb-6" data-id="element-207">
              A diferencia del sistema circulatorio, el sistema linfático
              (encargado de eliminar toxinas y líquidos retenidos) no tiene un
              "corazón" que bombee. Depende del movimiento y la gravedad.
            </p>

            <div className="bg-white p-6 rounded-2xl border border-gold/10 shadow-sm" data-id="element-208">
              <h5 className="font-bold text-darkBrown mb-3" data-id="element-209">
                Práctica Diaria (10 minutos):
              </h5>
              <ol className="space-y-4 text-darkBrown/70 text-sm" data-id="element-210">
                <li className="flex gap-3" data-id="element-211">
                  <span className="font-bold text-gold" data-id="element-212">1.</span>
                  Acuéstate en el suelo o en la cama, acercando tus caderas a la
                  pared.
                </li>
                <li className="flex gap-3" data-id="element-213">
                  <span className="font-bold text-gold" data-id="element-214">2.</span>
                  Eleva tus piernas y apóyalas rectas contra la pared, formando
                  una "L" con tu cuerpo.
                </li>
                <li className="flex gap-3" data-id="element-215">
                  <span className="font-bold text-gold" data-id="element-216">3.</span>
                  Abre los brazos en cruz, palmas hacia arriba.
                </li>
                <li className="flex gap-3" data-id="element-217">
                  <span className="font-bold text-gold" data-id="element-218">4.</span>
                  Respira profundamente desde el abdomen. Sentirás un alivio
                  inmediato en la pesadez de las piernas y ayudarás a drenar
                  líquidos acumulados.
                </li>
              </ol>
            </div>
          </motion.div>

          {/* Sleep Hygiene */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-219">
            <div className="flex items-center gap-4 mb-6" data-id="element-220">
              <div className="p-3 bg-darkBrown/10 rounded-xl text-darkBrown" data-id="element-221">
                <MoonStar className="w-8 h-8" data-id="element-222" />
              </div>
              <h4 className="font-serif text-3xl font-bold text-darkBrown" data-id="element-223">
                Higiene del Sueño
              </h4>
            </div>

            <p className="text-darkBrown/80 mb-6" data-id="element-224">
              Dormir mal es un estresor masivo. Una noche de mal sueño aumenta
              la resistencia a la insulina al día siguiente, haciéndote acumular
              grasa abdominal.
            </p>

            <div className="space-y-4" data-id="element-225">
              <div className="bg-white p-5 rounded-xl border border-gold/10 flex gap-4 items-start" data-id="element-226">
                <div className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0" data-id="element-227"></div>
                <div data-id="element-228">
                  <h6 className="font-bold text-darkBrown" data-id="element-229">Apagón Digital</h6>
                  <p className="text-sm text-darkBrown/70" data-id="element-230">
                    Apaga pantallas (TV, celular) 1 hora antes de dormir. La luz
                    azul bloquea la melatonina.
                  </p>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-gold/10 flex gap-4 items-start" data-id="element-231">
                <div className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0" data-id="element-232"></div>
                <div data-id="element-233">
                  <h6 className="font-bold text-darkBrown" data-id="element-234">
                    Temperatura Fresca
                  </h6>
                  <p className="text-sm text-darkBrown/70" data-id="element-235">
                    Mantén tu habitación fresca (alrededor de 18-20°C). El
                    cuerpo necesita bajar su temperatura para un sueño
                    reparador.
                  </p>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-gold/10 flex gap-4 items-start" data-id="element-236">
                <div className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0" data-id="element-237"></div>
                <div data-id="element-238">
                  <h6 className="font-bold text-darkBrown" data-id="element-239">Magnesio</h6>
                  <p className="text-sm text-darkBrown/70" data-id="element-240">
                    Considera un suplemento de Glicinato de Magnesio por la
                    noche. Relaja los músculos y el sistema nervioso.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}